/**
 * Inventory Xero Account Exception Config
 *
 * Covers: ex-inv-ls-xero-account-error
 *
 * Products with missing or incorrect Xero account mapping.
 * Account code columns shown in monospace red.
 */
import React from 'react'
import { Typography } from '@mui/material'
import { formatCurrency } from '../../../utils/inspectorFormatters'
import { poolwerxColors } from '../../../theme/colors'
import { PLATFORM_CONTEXT_COLUMNS } from '../shared/platformColumns'
import {
  activePart,
  composeRowSubtitle,
  skuPart,
  textPart,
} from '../shared/rowSubtitle'
import { MAIN_CATEGORY_COLUMN } from '../shared/productExceptionColumns'
import { PRODUCT_NAME_MIN_WIDTH, productStatusSortValue, renderProductName, renderProductStatus } from '../shared/productRenderers'
import type { ExceptionConfig, ExceptionColumnDef } from '../types'

const accountCodeStyle = () => ({
  fontFamily: 'monospace',
  color: poolwerxColors.red,
})

const renderAccountCode = (value: any): React.ReactNode => {
  return React.createElement(Typography, {
    variant: 'body2',
    sx: accountCodeStyle(),
  }, value || '-')
}

const columns: ExceptionColumnDef[] = [
  ...PLATFORM_CONTEXT_COLUMNS,
  {
    key: 'name',
    label: 'Product Name',
    align: 'left',
    minWidth: PRODUCT_NAME_MIN_WIDTH,
    render: renderProductName,
    link: { field: 'lightspeed_product_link', system: 'lightspeed' },
  },
  {
    key: 'active',
    label: 'Product Status',
    align: 'center',
    render: renderProductStatus,
    getSortValue: productStatusSortValue,
  },
  {
    key: 'sku',
    label: 'SKU',
    render: (value) => value || 'N/A',
  },
  MAIN_CATEGORY_COLUMN,
  {
    key: 'account_code',
    label: 'Sales Account',
    render: renderAccountCode,
    getCellStyle: () => accountCodeStyle(),
  },
  {
    key: 'account_code_purchase',
    label: 'Purchase Account',
    render: renderAccountCode,
    getCellStyle: () => accountCodeStyle(),
  },
  {
    key: 'price_including_tax',
    label: 'Price',
    type: 'currency',
    align: 'right',
    render: (value, row, ctx) => ctx.variant === 'inspector' ? formatCurrency(value) : undefined,
    exportFormat: 'currency',
  },
]

export const xeroAccountConfig: ExceptionConfig = {
  codes: ['ex-inv-ls-xero-account-error'],
  columns,
  defaultSortColumn: 'name',
  defaultSortDirection: 'asc',
  complexity: 'simple',
  rowKeyField: 'ls_id',
  getRowSubtitle: (row) =>
    composeRowSubtitle([
      skuPart(row.sku),
      activePart(row.active ?? row.is_active),
      textPart(row.xero_account_code) && `Xero ${textPart(row.xero_account_code)}`,
      textPart(row.error_summary),
    ]),
}
